<template>
  <section class="limiter-content assembly-page pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <header class="assembly-intro">
      <span class="assembly-eyebrow">
        <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
        {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
      </span>
      <h1>Assembleias <span>de credores</span></h1>
      <p>Consulte sua participação, entre na reunião e acompanhe as votações.</p>
    </header>
    <div v-if="dataset.description" class="assembly-description" v-html="dataset.description"></div>

    <v-form class="assembly-search" @submit.prevent="load()">
      <v-row align="center" dense>
        <v-col cols="12" md="5">
          <v-text-field dense label="Devedora" v-model="dataset.filter.client_name" prepend-inner-icon="mdi-magnify" hide-details="auto" outlined></v-text-field>
        </v-col>

        <v-col cols="12" md="5">
          <v-select dense :items="dataset.districts" label="Comarca" placeholder="Selecione uma Comarca"
            v-model="dataset.filter.district" item-value="id" item-text="name" outlined hide-details="auto"></v-select>
        </v-col>

        <v-col cols="12" md="2">
          <v-btn class="button-creditor-assembly" color="primary" depressed type="submit"
            :loading="dataset.loading">Buscar</v-btn>
        </v-col>
      </v-row>
    </v-form>

    <br />

    <!-- Procurador: assembleias agrupadas por caso, com os credores vinculados -->
    <section v-if="isProcurator" class="list-card">
      <!-- Atalhos das funcoes de AGC que sairam do menu -->
      <div class="agc-shortcuts">
        <router-link to="/credenciamentos" class="agc-shortcut">
          <v-icon small>mdi-account-check-outline</v-icon> Credenciamentos
        </router-link>
        <router-link to="/substabelecimentos" class="agc-shortcut">
          <v-icon small>mdi-account-switch-outline</v-icon> Substabelecimentos
        </router-link>
        <router-link to="/procurador/assembleia-do-caso" class="agc-shortcut">
          <v-icon small>mdi-account-group-outline</v-icon> Assembleia do caso
        </router-link>
      </div>
      <div class="assembly-section-heading">
        <h2>Meus casos</h2>
        <span>{{ procuratorCases.length }} caso(s)</span>
      </div>
      <v-progress-linear v-if="procuratorCasesLoading" indeterminate color="darkSecondary" class="mb-2"></v-progress-linear>
      <div v-else-if="!procuratorCases.length" class="as-empty">
        Nenhum caso com associação de procurador. Cadastre a associação em "Meus credores" para credenciar.
      </div>

      <v-expansion-panels v-else multiple flat class="case-panels">
        <v-expansion-panel v-for="item in procuratorCases" :key="item.client_id" class="case-panel">
          <v-expansion-panel-header>
            <div class="case-panel-head">
              <div>
                <div class="case-panel-title">{{ item.client_name }}</div>
                <div class="case-panel-sub">
                  <template v-if="item.current_session">
                    {{ item.current_session.session_label }} · {{ item.current_session.date | defaultDate('DD/MM/YYYY HH:mm', false, true) }} BRT
                    · <span :class="Number(item.current_session.status) === 5 ? 'as-ok-text' : ''">{{ sessionStatusLabel(item.current_session.status) }}</span>
                    <span v-if="item.current_session.running_votings"> · {{ item.current_session.running_votings }} votação(ões) em andamento</span>
                  </template>
                  <template v-else>Sem assembleia cadastrada</template>
                </div>
              </div>
              <div class="case-panel-chips">
                <span class="as-pill as-pill--muted">{{ item.totals.creditors }} credor(es)</span>
                <span class="as-pill as-pill--ok">{{ item.totals.accredited }} credenciado(s)</span>
                <span v-if="item.totals.pending_accreditation" class="as-pill as-pill--warn">{{ item.totals.pending_accreditation }} pendente(s)</span>
                <span v-if="item.totals.substituted" class="as-pill as-pill--info">{{ item.totals.substituted }} substabelecido(s)</span>
                <span v-if="item.totals.can_request" class="as-pill as-pill--outline">{{ item.totals.can_request }} pode(m) credenciar</span>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="table-scroll">
              <v-simple-table dense class="as-table">
                <thead>
                  <tr>
                    <th class="text-left">Credor</th>
                    <th class="text-left">CPF/CNPJ</th>
                    <th class="text-left">Associação</th>
                    <th class="text-left">Credenciamento</th>
                    <th class="text-left">Presença</th>
                    <th class="text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="creditor in item.creditors" :key="creditor.association_id">
                    <td class="as-strong as-capitalize">{{ creditor.creditor_name }}</td>
                    <td>{{ creditor.creditor_registration }}</td>
                    <td>
                      <span class="as-pill" :class="creditor.association_status === 1 ? 'as-pill--ok' : 'as-pill--warn'">
                        <span class="as-pill__dot"></span>{{ creditor.association_status === 1 ? 'Aprovada' : (creditor.association_status === 3 ? 'Regularização solicitada' : 'Em análise') }}
                      </span>
                    </td>
                    <td>
                      <span v-if="creditor.continuation_blocked" class="as-pill as-pill--danger"><span class="as-pill__dot"></span>Sem direito (ausente antes)</span>
                      <span v-else-if="creditor.accreditation" class="as-pill" :class="`as-pill--${accreditationTone(creditor.accreditation.status)}`">
                        <span class="as-pill__dot"></span>{{ getAccreditationStatus(creditor.accreditation.status) }}
                      </span>
                      <span v-else class="as-muted">Não solicitado</span>
                      <button v-if="creditor.substitution" type="button" class="as-pill as-pill--button ml-1" :class="creditor.substitution.state === 'approved' ? 'as-pill--info' : 'as-pill--warn'"
                        :title="substitutionTitle(creditor.substitution)" @click="openSubstitution(creditor.substitution)">
                        <v-icon x-small>mdi-account-switch-outline</v-icon> {{ substitutionChipLabel(creditor.substitution) }}
                      </button>
                    </td>
                    <td>
                      <!-- Presenca so depois que a assembleia foi aberta (a API manda null antes) -->
                      <span v-if="creditor.accreditation && Number(creditor.accreditation.status) === 1 && creditor.present !== null" class="as-pill" :class="creditor.present ? 'as-pill--ok' : 'as-pill--muted'">
                        <span class="as-pill__dot"></span>{{ creditor.present ? 'Presente' : 'Sem presença' }}
                      </span>
                      <span v-else class="as-muted">—</span>
                      <div v-if="creditor.substitution && !creditor.represented_by_me" class="as-muted mt-1">por {{ creditor.current_procurator }}</div>
                    </td>
                    <td class="text-right">
                      <v-btn v-if="creditor.can_request_accreditation && item.current_session" small text rounded color="darkSecondary" class="as-action"
                        @click="openAccreditationFor(item, creditor)">Novo credenciamento</v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
            <div v-if="item.current_session && item.current_session.accreditation_availability && !item.current_session.accreditation_availability.is_open" class="text-caption grey--text mt-2">
              {{ item.current_session.accreditation_availability.message }}
            </div>
            <div v-if="substitutedCreditors(item).length" class="text-caption mt-2 substitution-note">
              <strong>Substabelecimentos nesta sessão:</strong>
              <div v-for="creditor in substitutedCreditors(item)" :key="`sub-${creditor.association_id}`">
                {{ creditor.creditor_name }} → {{ creditor.substitution.new_procurator_name }}
                · {{ substitutionModalityLabel(creditor.substitution.modality) }}
                · <a href="#" @click.prevent="openSubstitution(creditor.substitution)">{{ substitutionStatusLabel(creditor.substitution) }}</a>
                <span v-if="creditor.substitution.valid_until"> · válido até {{ creditor.substitution.valid_until | defaultDate('DD/MM/YYYY') }}</span>
                <span v-if="!creditor.represented_by_me"> — o credor passou a ser representado por {{ creditor.current_procurator }}</span>
              </div>
            </div>
            <div class="case-panel-actions">
              <v-btn text rounded color="darkSecondary" class="as-action" @click="openCasePage(item)">
                <v-icon left small>mdi-open-in-new</v-icon> Acessar caso
              </v-btn>
              <v-btn v-if="item.current_session && item.totals.can_request" color="primary" depressed class="as-btn" @click="openAccreditationFor(item, null)">
                <v-icon left small>mdi-account-plus-outline</v-icon> Novo credenciamento
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <section class="list-card">
      <!-- <span><strong>Próximas</strong></span> -->
      <div class="assembly-section-heading"><h2>Próximas assembleias</h2><span>{{ dataset.currentGeneralMeetings.length }} assembleia(s)</span></div>
      <div v-show="!dataset.currentGeneralMeetings.length" class="as-empty">Nenhuma assembleia próxima foi encontrada.</div>
      <article v-for="(item, index) in dataset.currentGeneralMeetings" :key="index"
        :ref="`assembly-item-${item.id}`" :prepend-icon="item.showAssembly ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        class="assembly-list" :class="{ 'assembly-list--expanded': item.showAssembly, 'assembly-list--running': Number(item.status) === 5 }">
        <button type="button" class="assembly-trigger" :aria-expanded="String(!!item.showAssembly)" @click="toggleAnswer(item)">
            <div class="assembly-card-heading">
              <div class="assembly-date" aria-label="Data da assembleia">
                <strong>{{ item.date | defaultDate('DD', false, true) }}</strong>
                <span>{{ item.date | defaultDate('MMM YYYY', false, true) }}</span>
                <small>{{ item.date | defaultDate('HH:mm', false, true) }} BRT</small>
              </div>
              <div class="assembly-card-title">
                <span class="assembly-case">{{ item.client.name }}</span>
                <h3>{{ item.name }}</h3>
              </div>
              <div class="assembly-card-status">
                <span class="as-pill" :class="`as-pill--${meetingTone(item.status)}`"><span class="as-pill__dot"></span>{{ item.status | enum(dataset.generalMeetingStatus, "id", "name") }}</span>
                <v-icon color="primary" aria-hidden="true">{{ item.showAssembly ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
            </div>
        </button>

        <v-expand-transition>
          <div class="assembly-collapse" v-show="item.showAssembly">
            <div class="assembly-details">

	                <div class="block-text">
	                  <div v-if="item.description" class="meeting-detail-description"><span class="detail-label">Sobre a assembleia</span><div v-html="item.description"></div></div>

                    <div v-if="canRequestAccreditation(item) || item.has_accreditation">


                      <p v-if="canRequestAccreditation(item)" class="mb-0">
                        Para realizar o credenciamento,
                        <a href="#" @click.prevent.stop="openAccreditationPage(item.id)">
                          <strong>clique aqui</strong>
                        </a>
                      </p>

                      <div v-else-if="item.continuation_blocked" class="participation-overview">
  <v-chip small label color="error" text-color="white">
    <v-icon left small>mdi-cancel</v-icon> Sem direito de participar desta sessão
  </v-chip>
  <p class="text-caption red--text text--darken-1 mt-2 mb-0">{{ item.continuation_blocked_reason }}</p>
</div>
<div v-else-if="item.has_accreditation" class="participation-overview">
                        <div class="participation-item">
                          <span class="detail-label">Credenciamento</span>
                          <strong :class="{ 'participation-ok': Number(item.status_accreditation) === 1 }">{{ getAccreditationStatus(item.status_accreditation) }}</strong>
                        </div>
                        <!-- Presenca so depois que a assembleia foi aberta -->
                        <div v-if="Number(item.status_accreditation) === 1 && item.presence_visible" class="participation-item">
                          <span class="detail-label">Presença</span>
                          <strong :class="{ 'participation-ok': item.has_presence }"><v-icon small :color="item.has_presence ? 'green darken-3' : 'grey'">{{ item.has_presence ? 'mdi-check-circle-outline' : 'mdi-clock-outline' }}</v-icon>{{ item.has_presence ? 'Confirmada' : 'Aguardando confirmação' }}</strong>
                        </div>
                        <div v-if="Number(item.status_accreditation) === 1" class="participation-item">
                          <span class="detail-label">Votação</span>
                          <strong>{{ item.running_voting_id ? item.running_voting_name : 'Nenhuma liberada' }}</strong>
                        </div>
                      </div>

                      <div v-if="!item.continuation_blocked && (canJoinZoom(item) || (item.running_voting_id && Number(item.status_accreditation) === 1))" class="assembly-actions">
                        <v-btn v-if="canJoinZoom(item)" color="primary" depressed :loading="zoom.loadingId === item.id" @click.stop="joinZoom(item)">
                          <v-icon left small>mdi-video-outline</v-icon>
                          Entrar na reunião
                        </v-btn>
                        <v-btn v-if="item.running_voting_id && Number(item.status_accreditation) === 1" color="primary" outlined @click.stop="goToVoting(item)">
                          <v-icon left small>mdi-vote</v-icon>
                          {{ item.has_presence ? 'Votar' : 'Ver votação' }}
                        </v-btn>
                      </div>
                    </div>
                    <div v-else-if="user && !item.has_accreditation && !item.continuation_blocked" class="as-notice mt-4">
                      <v-icon small color="#7e55ab">mdi-information-outline</v-icon>
                      {{ accreditationAvailability(item).message }}
                    </div>
                    <!-- Lista de presencas publica: liberada no inicio da sessao -->
                    <div v-if="item.presence_list_published_at" class="presence-list-link mt-4">
                      <v-btn outlined color="primary" :href="presenceListUrl(item.id)" target="_blank" rel="noopener" @click.stop>
                        <v-icon left small>mdi-file-document-outline</v-icon>
                        Lista de presença (PDF)
                      </v-btn>
                      <span class="detail-label">Publicada em {{ item.presence_list_published_at | defaultDate('DD/MM/YYYY HH:mm') }} BRT</span>
                    </div>
                </div>
            </div>
          </div>
        </v-expand-transition>
      </article>
    </section>


    <section class="list-card">
      <div class="assembly-section-heading"><h2>Assembleias anteriores</h2><span>{{ dataset.previousGeneralMeetings.length }} assembleia(s)</span></div>
      <div v-show="!dataset.previousGeneralMeetings.length" class="as-empty">Nenhuma assembleia anterior foi encontrada.</div>

      <article v-for="(item, index) in dataset.previousGeneralMeetings" :key="index"
        :ref="`assembly-item-${item.id}`" :prepend-icon="item.showAssembly ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        class="assembly-list" :class="{ 'assembly-list--expanded': item.showAssembly, 'assembly-list--running': Number(item.status) === 5 }">
        <!-- @click="toggleAnswer(index)" -->
        <button type="button" class="assembly-trigger" :aria-expanded="String(!!item.showAssembly)" @click="toggleAnswer(item)">
            <div class="assembly-card-heading">
              <div class="assembly-date" aria-label="Data da assembleia">
                <strong>{{ item.date | defaultDate('DD', false, true) }}</strong>
                <span>{{ item.date | defaultDate('MMM YYYY', false, true) }}</span>
                <small>{{ item.date | defaultDate('HH:mm', false, true) }} BRT</small>
              </div>
              <div class="assembly-card-title">
                <span class="assembly-case">{{ item.client.name }}</span>
                <h3>{{ item.name }}</h3>
              </div>
              <div class="assembly-card-status">
                <span class="as-pill" :class="`as-pill--${meetingTone(item.status)}`"><span class="as-pill__dot"></span>{{ item.status | enum(dataset.generalMeetingStatus, "id", "name") }}</span>
                <v-icon color="primary" aria-hidden="true">{{ item.showAssembly ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
            </div>
        </button>

        <v-expand-transition>
          <div class="assembly-collapse" v-show="item.showAssembly">
            <div class="assembly-details">

                <div class="block-text">
                  <div v-if="item.description" class="meeting-detail-description"><span class="detail-label">Sobre a assembleia</span><div v-html="item.description"></div></div>
                  <!-- Lista de presencas publica: liberada no inicio da sessao -->
                  <div v-if="item.presence_list_published_at" class="presence-list-link mt-4">
                    <v-btn outlined color="primary" :href="presenceListUrl(item.id)" target="_blank" rel="noopener" @click.stop>
                      <v-icon left small>mdi-file-document-outline</v-icon>
                      Lista de presença (PDF)
                    </v-btn>
                    <span class="detail-label">Publicada em {{ item.presence_list_published_at | defaultDate('DD/MM/YYYY HH:mm') }} BRT</span>
                  </div>
                </div>
            </div>
          </div>
        </v-expand-transition>
      </article>
    </section>

    <v-dialog v-if="dataset.selectedNextGeneralMeeting && !isProcurator" v-model="showModal" max-width="580" content-class="next-assembly-dialog" aria-labelledby="next-assembly-title">
      <v-card class="next-assembly-card">
        <header class="next-assembly-heading">
          <div>
            <span class="next-assembly-eyebrow">SUA AGENDA</span>
            <h2 id="next-assembly-title">Próxima assembleia</h2>
          </div>
          <v-btn icon aria-label="Fechar próxima assembleia" @click="closeModal"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <div class="next-assembly-body">
          <div class="next-assembly-schedule">
            <v-icon color="primary" size="28" aria-hidden="true">mdi-calendar-outline</v-icon>
            <div><span>DATA</span><strong>{{ dataset.selectedNextGeneralMeeting.date | defaultDate('DD/MM/YYYY', false, true) }}</strong></div>
            <div class="next-assembly-time"><span>HORÁRIO DE BRASÍLIA</span><strong>{{ dataset.selectedNextGeneralMeeting.date | defaultDate('HH:mm', false, true) }} <small>BRT</small></strong></div>
          </div>
          <div class="next-assembly-context">
            <span class="detail-label">{{ dataset.selectedNextGeneralMeeting.client_name }}</span>
            <h3>{{ dataset.selectedNextGeneralMeeting.name }}</h3>
            <v-chip small outlined :color="generalMeetingStatusColor(dataset.selectedNextGeneralMeeting.status)">{{ dataset.selectedNextGeneralMeeting.status | enum(dataset.generalMeetingStatus, "id", "name") }}</v-chip>
          </div>
          <div class="next-assembly-accreditation" :class="{ 'next-assembly-accreditation--approved': isAccreditationApproved(dataset.selectedNextGeneralMeeting) }">
            <v-icon :color="isAccreditationApproved(dataset.selectedNextGeneralMeeting) ? 'green darken-3' : 'primary'" aria-hidden="true">{{ isAccreditationApproved(dataset.selectedNextGeneralMeeting) ? 'mdi-check-circle-outline' : 'mdi-information-outline' }}</v-icon>
            <div>
              <strong v-if="isAccreditationApproved(dataset.selectedNextGeneralMeeting)">Credenciamento aprovado</strong>
              <strong v-else-if="dataset.selectedNextGeneralMeeting.has_accreditation">Credenciamento {{ getAccreditationStatus(dataset.selectedNextGeneralMeeting.status_accreditation) }}</strong>
              <strong v-else>Credenciamento</strong>
              <p v-if="isAccreditationApproved(dataset.selectedNextGeneralMeeting)">Seu credenciamento para esta assembleia foi aprovado.</p>
              <p v-else-if="canRequestAccreditation(dataset.selectedNextGeneralMeeting)">Solicite seu credenciamento para participar desta assembleia.</p>
              <p v-else-if="!dataset.selectedNextGeneralMeeting.has_accreditation">{{ accreditationAvailability(dataset.selectedNextGeneralMeeting).message }}</p>
            </div>
          </div>
          <section v-if="dataset.nextGeneralMeetings.length" class="next-assembly-upcoming">
            <h3>Outras datas na sua agenda</h3>
            <div class="next-assembly-events">
              <article v-for="item in dataset.nextGeneralMeetings" :key="item.id" class="next-assembly-event">
                <span class="detail-label">{{ item.date | defaultDate('DD/MM/YYYY HH:mm', false, true) }} BRT</span>
                <strong>{{ item.name }}</strong>
                <p>{{ item.client_name }}</p>
                <v-chip small outlined :color="generalMeetingStatusColor(item.status)">{{ item.status | enum(dataset.generalMeetingStatus, "id", "name") }}</v-chip>
                <p v-if="isAccreditationApproved(item)" class="mt-2">Credenciamento aprovado.</p>
                <p v-else-if="item.has_accreditation" class="mt-2">Credenciamento {{ getAccreditationStatus(item.status_accreditation) }}</p>
                <v-btn v-else-if="canRequestAccreditation(item)" text small color="primary" @click="openAccreditationPage(item.id)">Solicitar credenciamento</v-btn>
              </article>
            </div>
          </section>
        </div>
        <v-card-actions class="next-assembly-footer">
          <v-btn text color="primary" @click="closeModal">Fechar</v-btn>
          <v-btn v-if="canRequestAccreditation(dataset.selectedNextGeneralMeeting)" depressed color="primary" @click="openAccreditationPage(dataset.selectedNextGeneralMeeting.id)">Solicitar credenciamento</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <v-row>
      <whatsButton />
    </v-row>

    <v-row>
      <topButton />
    </v-row>
    <v-dialog v-model="zoom.dialog" max-width="560">
      <v-card>
        <v-card-title>Entrar na assembleia</v-card-title>
        <v-card-text v-if="zoom.meeting">
          <p>Selecione o credor representado. O acesso será validado conforme seu credenciamento nesta assembleia.</p>
          <procurator-creditor-selector v-model="zoom.creditorId" :client-id="zoom.meeting.client_id" require-client />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="zoom.dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!zoom.creditorId" :loading="!!zoom.loadingId" @click="joinZoom(zoom.meeting, zoom.creditorId)">Entrar na reunião</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import 'moment/locale/pt-br';
import topButton from '@/components/layout/GoToTopButton.vue';
import whatsButton from '@/components/layout/WhatsAppButton.vue';

import object from '@/common/util/object';
import axios from 'axios';
import ProcuratorCreditorSelector from '@/components/ProcuratorCreditorSelector.vue';

import generalMeetingStatus from "@/common/data/general-meeting-status.data"
import SweetScroll from 'sweet-scroll';

export default {
  components: {
    ProcuratorCreditorSelector,
    topButton,
    whatsButton,
  },

  data() {
    return {
      user: null,
      // Procurador: casos com associacao, com credores e situacao na sessao atual.
      procuratorCases: [],
      procuratorCasesLoading: false,
      dataset: {
        filter: {
          client_name: '',
          district: '',
        },
        generalMeetingStatus: generalMeetingStatus,
        description: '',
        generalMeetings: [],
        previousGeneralMeetings: [],
        currentGeneralMeetings: [],
        districts: [],
        states: [],
        options: {
          page: 1,
          itemsPerPage: 50,
          sortBy: ['name'],
          sortDesc: [false]
        },
        nextGeneralMeetings: [],
        selectedNextGeneralMeeting: null,
        count: 0,
        loading: false
      },
      showModal: false,
      modalTitle: '',
      modalDate: '',
      assemblyName: '',
      futureMeetings: [],
      zoom: {
        dialog: false, meeting: null, creditorId: null,
        loadingId: null,
      },

    };
  },

  computed: {
    isProcurator() {
      return !!this.user && Number(this.user.type) === 2;
    },
  },

	  mounted() {
      this.user = this.$root.user;

	    this.loadData();

    const eventId = this.$route.query.eventId;
    if (eventId) {
      setTimeout(() => {
        this.openSpecificCollapse(eventId);
      }, 1000)
    }
  },

  // created() {
  //   this.$ga.event({
  //     eventCategory: 'page',
  //     eventAction: 'action',
  //     eventLabel: 'click',
  //     eventValue: 'contact'
  //   })

  //   this.loadData()
  // },

  methods: {
    // PDF publico da lista de presencas (rota sem login na API).
    presenceListUrl(meetingId) {
      const base = String(axios.defaults.baseURL || '').replace(/\/+$/, '');
      return `${base}/api/general-meetings/${meetingId}/presence-list`;
    },


    openSpecificCollapse(eventId) {
      const eventIndex = this.dataset.generalMeetings.findIndex(event => event.id == eventId);
      if (eventIndex !== -1) {
        this.$set(this.dataset.generalMeetings[eventIndex], 'showAssembly', true);
        this.scrollToEvent(eventId);
      }
    },

    scrollToEvent(eventId) {
      this.$nextTick(() => {
        const assemblyItem = this.$refs[`assembly-item-${eventId}`];
        if (assemblyItem && assemblyItem[0]) {
          const scroller = new SweetScroll();
          scroller.toElement(assemblyItem[0]);
        }
      });
    },

    openAccreditationPage(generalMeetingId) {
      this.$router.push({
        path: '/assembleia-credores/credenciamento',
        query: { id: generalMeetingId }
      });
    },

    getNextMeeting() {
      const today = this.$moment();
      const futureMeetings = this.dataset.generalMeetings
        .filter(meeting => this.$moment(meeting.date).isAfter(today))
        .sort((a, b) => this.$moment(a.date).valueOf() - this.$moment(b.date).valueOf());

      return futureMeetings.length > 0 ? futureMeetings[0] : null;
    },

    // Tons das pilulas (mesmas faixas das cores anteriores).
    accreditationTone(status) {
      return { 1: 'ok', 2: 'danger' }[Number(status)] || 'warn';
    },
    meetingTone(status) {
      return { 0: 'info', 1: 'info', 2: 'danger', 3: 'warn', 4: 'muted', 5: 'ok' }[Number(status)] || 'muted';
    },
    goToVoting(item) {
      if (!item || !item.running_voting_id) return;
      this.$router.push(`/assembleia-credores/votacao/${item.running_voting_id}`);
    },
	    getAccreditationStatus(status) {
      switch (status) {
        case 0:
          return 'Pendente'
        case 1:
          return 'Aprovado'
        case 2:
          return 'Rejeitado'
        case 3:
          return 'Regularização solicitada'
        default:
          return ''
      }
	    },

      isAccreditationApproved(item) {
        return !!item && Number(item.status_accreditation) === 1;
      },

	    generalMeetingStatusColor(status) {
      switch (status) {
        case 0:
          return 'primary'
        case 1:
          return 'primary'
        case 2:
          return 'error'
        case 3:
          return 'warning'
        case 4:
          return 'grey'
        case 5:
          return 'green'
        default:
          break;
      }

	    },

      canRequestAccreditation(item) {
        return !!this.user && !item.has_accreditation && this.accreditationAvailability(item).isOpen;
      },

      accreditationAvailability(item) {
        if (!item) return { isOpen: false, message: 'Assembleia não encontrada.' };
        if (item.accreditation_availability) {
          return {
            isOpen: !!item.accreditation_availability.is_open,
            message: item.accreditation_availability.message,
          };
        }
        if (item.accreditation_enabled === false || Number(item.accreditation_enabled) === 0) {
          return { isOpen: false, message: 'O credenciamento não está habilitado para esta assembleia.' };
        }
        if (item.accreditation_closed_at) {
          return { isOpen: false, message: 'O credenciamento foi encerrado pela administração judicial.' };
        }
        if (item.accreditation_starts_at && this.$moment(item.accreditation_starts_at).isAfter()) {
          return { isOpen: false, message: `O credenciamento será aberto em ${this.$moment(item.accreditation_starts_at).format('DD/MM/YYYY [às] HH:mm')} (BRT).` };
        }
        if (item.accreditation_ends_at && !this.$moment(item.accreditation_ends_at).isAfter()) {
          return { isOpen: false, message: `O prazo para credenciamento encerrou em ${this.$moment(item.accreditation_ends_at).format('DD/MM/YYYY [às] HH:mm')} (BRT).` };
        }
        if (Number(item.status) !== 0) {
          return { isOpen: false, message: 'Esta assembleia não está aberta para novos credenciamentos.' };
        }
        return { isOpen: true, message: 'Credenciamento aberto.' };
      },

      canJoinZoom(item) {
        if (!this.user || !item || !item.zoom_enabled) return false;
        if (this.isProcurator) return !!item.client_id && Number(item.status) === 5;
        return !!item.has_accreditation && [1, 2].includes(Number(item.status_accreditation));
      },

      async joinZoom(item, creditorUserId = null) {
        if (this.isProcurator && !creditorUserId) {
          this.zoom.meeting = item; this.zoom.creditorId = null; this.zoom.dialog = true; return;
        }
        this.zoom.loadingId = item.id;

        try {
          const result = await axios.post(`/api/general-meetings/${item.id}/zoom/join`, { creditor_user_id: creditorUserId || undefined });
          const joinUrl = result.data.content && result.data.content.join_url;

          if (!joinUrl) {
            this.$dialog.notify.error('Link individual da reunião não disponível.', { position: 'top-right', timeout: 5000 });
            return;
          }

          window.open(joinUrl, '_blank', 'noopener,noreferrer');
          this.zoom.dialog = false;
        } catch (error) {
          this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
        } finally {
          this.zoom.loadingId = null;
        }
      },

    async loadData() {
      await this.loadDistricts();
      await this.load();
      if (this.isProcurator) this.loadProcuratorCases();

      if (this.$root.user)
        await this.loadNextGeneralMeetings();

      await this.loadText();
    },

    toggleAnswer(item) {
      this.$set(item, 'showAssembly', item.showAssembly == undefined ? true : !item.showAssembly);
    },

    refreshMeetingGroups() {
      this.dataset.previousGeneralMeetings = this.dataset.generalMeetings.filter((x) => this.$moment(x.date).isBefore())
      this.dataset.currentGeneralMeetings = this.dataset.generalMeetings.filter((x) => this.$moment(x.date).isAfter())
    },

    mergeAccreditationStatus(generalMeetings) {
      const accreditationStatusByMeeting = generalMeetings.reduce((acc, item) => {
        acc[item.id] = {
          has_accreditation: item.has_accreditation,
          status_accreditation: item.status_accreditation,
          participation_type: item.participation_type,
          // Presenca concedida e votacao liberada tambem vem de /api/general-meetings/next.
          has_presence: item.has_presence,
          presence_visible: item.presence_visible,
          running_voting_id: item.running_voting_id,
          running_voting_name: item.running_voting_name,
          continuation_blocked: item.continuation_blocked,
          continuation_blocked_reason: item.continuation_blocked_reason,
        };

        return acc;
      }, {});

      this.dataset.generalMeetings = this.dataset.generalMeetings.map((item) => {
        const accreditationStatus = accreditationStatusByMeeting[item.id];

        if (!accreditationStatus) return item;

        return {
          ...item,
          ...accreditationStatus,
        };
      });

      this.refreshMeetingGroups();
    },

    submitForm() {
      const emptyForm = Object.values(this.formData).every(value => !value);

      if (emptyForm) {
        // this.openModal('Campo Obrigatório', 'Preencha ao menos um campo para pesquisa.');
        return;
      }

      this.clearForm();
    },

    clearForm() {
      this.formData = {
        find: '',
        comarca: '',
        uf: ''
      };
    },

    async loadProcuratorCases() {
      this.procuratorCasesLoading = true;
      try {
        const result = await axios.get('/api/procurators/agc-cases');
        this.procuratorCases = (result.data.content && result.data.content.cases) || [];
      } catch (error) {
        this.procuratorCases = [];
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 4000 });
      } finally {
        this.procuratorCasesLoading = false;
      }
    },

    // Novo credenciamento na sessao atual do caso; com credor definido, ja chega selecionado.
    openAccreditationFor(item, creditor) {
      const query = { id: item.current_session.general_meeting_id };
      if (creditor && creditor.creditor_user_id) query.creditor_user_id = creditor.creditor_user_id;
      this.$router.push({ path: '/assembleia-credores/credenciamento', query });
    },

    substitutedCreditors(item) {
      return (item.creditors || []).filter((creditor) => creditor.substitution);
    },

    // Abre o pedido na pagina de substabelecimentos.
    openSubstitution(substitution) {
      this.$router.push({ path: '/substabelecimentos', query: { id: substitution.id } });
    },

    substitutionChipLabel(substitution) {
      return substitution.state === 'approved' ? 'Substabelecimento aprovado' : 'Substabelecimento pendente';
    },

    substitutionStatusLabel(substitution) {
      const names = { draft: 'rascunho', submitted: 'em análise', under_review: 'em análise', waiting_documents: 'aguardando documentos', waiting_registration: 'aguardando cadastro do novo procurador', approved: 'aprovado' };
      return names[substitution.status] || substitution.status;
    },

    substitutionModalityLabel(modality) {
      return modality === 'without_reservation' ? 'sem reserva' : 'com reserva';
    },

    substitutionTitle(substitution) {
      return `Protocolo ${substitution.protocol || '-'} · ${substitution.new_procurator_name} · ${this.substitutionModalityLabel(substitution.modality)} · ${this.substitutionStatusLabel(substitution)}. Clique para ver o pedido.`;
    },

    // Pagina consolidada do caso, em nova aba.
    openCasePage(item) {
      const route = this.$router.resolve({ path: `/procurador/casos/${item.client_id}/assembleia` });
      window.open(route.href, '_blank');
    },

    sessionStatusLabel(status) {
      return { 0: 'Aberta', 1: 'Fechada', 2: 'Interrompida', 3: 'Suspensa', 4: 'Finalizada', 5: 'Em andamento' }[Number(status)] || '-';
    },

    async loadDistricts() {
      try {
        const result = await axios.get(`/api/districts`, {
          params: { start: 1, limit: 100000, search: {} }
        });

        this.dataset.districts = [{ id: "", name: "Todas" }, ...result.data.content.data];
      } catch (error) {

        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 2000
        });
      }
    },

    async load() {
      let search = Object.fromEntries(Object.entries(this.dataset.filter).filter(([_, v]) => v));

      search = !object.isEmpty(search) ? search : null;

      this.dataset.loading = true;

      var limit = this.dataset.options.itemsPerPage;
      var start = (this.dataset.options.page - 1) * limit;

      try {
        const result = await axios.get('/api/general-meetings', {
          params: { start: start, limit: limit, search: search }
        });

        this.dataset.generalMeetings = result.data.content.map(x => {
          return { ...x, showAssembly: false }
        });

          this.refreshMeetingGroups();

      } catch (error) {

        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
      } finally {
        this.dataset.loading = false;
      }

      const eventId = this.$route.query.eventId;
      if (eventId) {
        this.openSpecificCollapse(eventId);
      }
    },

    async loadNextGeneralMeetings() {
      try {
        const result = await axios.get('/api/general-meetings/next');

        const data = result.data.content.data;

          this.mergeAccreditationStatus(data);

        // O procurador ve os chips de credenciamento/presenca/votacao, mas nao o modal.
        if (this.isProcurator) {
          this.dataset.selectedNextGeneralMeeting = null;
          this.dataset.nextGeneralMeetings = [];
          this.showModal = false;
          return;
        }

        if (data.length) {
          const [selectedNextGeneralMeeting, ...nextGeneralMeetings] = data;

          this.dataset.selectedNextGeneralMeeting = selectedNextGeneralMeeting;

          this.dataset.nextGeneralMeetings = nextGeneralMeetings;
          this.openModal();
        }


      } catch (error) {

        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
      } finally {
        this.dataset.loading = false;
      }
    },

    openModal() {
      // this.modalTitle = title;
      // this.modalMessage = message;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async loadText() {
      try {
        const result = await axios.get(`/api/dynamic-infos/keys`, {
          params: { keys: 'general-meeting-description' }
        });

        this.dataset.description = result.data.content && result.data.content.length > 0 ? result.data.content[0].value : '';

      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 2000
        });
      }
    }
  }

};
</script>

<style scoped>
.title-page {
  color: var(--v-primary-base);
  font-family: Metropolis, sans-serif;
  font-size: 30px !important;
  font-style: normal;
  font-weight: 700;
  line-height: 38px;
}

.assembly-list {
  cursor: pointer;
  border-bottom: 1px solid var(--v-grey-lighten-2);
  margin-bottom: 20px;

}

.assembly-box {
  font-family: Metropolis, sans-serif;
  background-color: #ddd;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  padding: 10px;
  width: 100%;
  box-sizing: border-box;
  line-height: 1.375rem;
}

.assembly-title {
  width: 10px;
  margin-left: 10px;
}

.block-text {
  background-color: white;
  color: black;
  font-family: Metropolis, sans-serif;
  font-size: 16px !important;
  text-align: justify;
}

.card-body {
  padding: 20px;
}

.clicked-item {
  background-color: white !important;
  color: black !important;
  font-family: Metropolis, sans-serif;
  font-size: 22px !important;
}

.assembly-header {
  background-color: white !important;
  color: black !important;
  font-family: Metropolis, sans-serif;
  font-size: 16px !important;
}

.small-image {
  margin-right: 10px;
  width: 30px !important;
}

.formFields {
  width: 100%;
  margin-bottom: -30px;
}

.findForm {
  width: 90%;
  margin-right: -15px;
  margin-left: 15px;
}

.button-creditor-assembly {
  margin-right: 0px !important;
  font-size: 16px !important;
  width: 150px;
  height: 38px !important;
  color: white !important;
  background-color: #009986 !important;
  transition: background-color 0.3s ease;

  @media (max-width: 600px) {
    width: 100%;
  }
}

.list-card {
  padding: 0 !important;
}

.findButton:hover {
  background-color: #0077cc !important;
}

.future-meeting-card {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  font-size: 0.9em;

  @media (max-width: 600px) {
    font-size: 0.7em;
  }
}

.timeline-scroll-container {
  max-height: 30rem;
  overflow-y: auto;
}


.assembly-page { padding-bottom: 48px !important; }
.assembly-page > .padding-block { height: 24px; }
.assembly-intro { padding: 12px 0 28px; }
.assembly-eyebrow { display: flex; gap: 8px; align-items: center; font-size: 11px; font-weight: 700; letter-spacing: .12em; color: #766386; }
.assembly-intro h1 { font-size: clamp(28px, 3.4vw, 40px); color: #25113c; letter-spacing: -.035em; line-height: 1.2; margin: 12px 0; }
.assembly-intro h1 span { color: #806697; }
.assembly-intro p { font-size: 14px; color: #73677c; margin: 0; }
.assembly-description { margin-bottom: 24px; }
.assembly-search { padding: 20px; border: 1px solid #e9e2ef; border-radius: 14px; background: #faf8fc; }
.assembly-search .button-creditor-assembly { width: 100%; height: 40px !important; margin: 0; background: #25113c !important; text-transform: none; letter-spacing: 0; border-radius: 8px; font-size: 14px !important; }
.assembly-section-heading { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin: 18px 0; }
.assembly-section-heading h2 { font-size: 21px; color: #2b193d; letter-spacing: -.02em; }
.assembly-section-heading > span { color: #82758d; font-size: 12px; }
.assembly-list { border: 1px solid #e8e1ee; border-radius: 14px; overflow: hidden; margin-bottom: 14px; background: white; cursor: auto; }
.assembly-list--expanded { box-shadow: 0 8px 28px -16px #33144d30; border-color: #cfc0de; }
.assembly-trigger { padding: 20px !important; }
.assembly-trigger > .v-list-item__content { padding: 0; }
.assembly-card-heading { display: flex; align-items: center; gap: 22px; width: 100%; }
.assembly-date { flex: 0 0 84px; display: flex; align-items: center; flex-direction: column; border-radius: 10px; padding: 12px 6px; background: #f4eff8; color: #422957; }
.assembly-date strong { font-size: 28px; line-height: 1.1; }
.assembly-date span { font-size: 11px; margin: 5px 0 8px; text-transform: uppercase; }
.assembly-date small { font-size: 10px; color: #79688a; }
.assembly-card-title { flex: 1; min-width: 0; }
.assembly-case { display: block; font-size: 12px; color: #7b6b87; line-height: 1.6; margin-bottom: 6px; overflow-wrap: anywhere; }
.assembly-card-title h3 { font-size: 17px; line-height: 1.45; color: #25113c; font-weight: 600; overflow-wrap: anywhere; }
.assembly-card-status { display: flex; align-items: center; gap: 16px; flex-shrink: 0; }
.assembly-card-status .v-chip { font-size: 11px; font-weight: 600; }
.assembly-details { padding: 0 24px 24px !important; border-top: 1px solid #eee8f3; }
.assembly-details > .v-list-item__content { padding: 20px 0 0; }
.assembly-details .block-text { padding: 0; font-size: 14px !important; line-height: 1.7; text-align: left; color: #675a72; overflow-wrap: anywhere; }
.participation-status { display: flex; flex-wrap: wrap; gap: 8px; }
.participation-status .v-chip { max-width: 100%; height: auto; min-height: 30px; padding: 6px 10px; }
.participation-status ::v-deep .v-chip__content { white-space: normal; }
.assembly-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
.presence-list-link { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.presence-list-link .v-btn { min-height: 44px; border-radius: 8px; text-transform: none; letter-spacing: 0; }
.assembly-actions .v-btn { min-height: 44px; border-radius: 8px; text-transform: none; letter-spacing: 0; }
.assembly-header { border: 1px dashed #ded4e7; border-radius: 12px; padding: 20px; min-height: 64px; height: auto; font-size: 14px !important; color: #7b6b87 !important; }
@media (max-width: 600px) {
  .assembly-search { padding: 14px; }
  .assembly-section-heading h2 { font-size: 18px; }
  .assembly-section-heading > span { font-size: 10px; }
  .assembly-trigger { padding: 16px !important; }
  .assembly-card-heading { display: grid; grid-template-columns: 64px minmax(0, 1fr); gap: 12px; }
  .assembly-date { padding: 10px 4px; grid-row: span 2; }
  .assembly-date strong { font-size: 25px; }
  .assembly-date span { font-size: 9px; }
  .assembly-card-title h3 { font-size: 15px; }
  .assembly-case { font-size: 11px; }
  .assembly-card-status { grid-column: 2; justify-content: space-between; }
  .assembly-details { padding: 0 16px 20px !important; }
  .assembly-actions { flex-direction: column; }
  .assembly-actions .v-btn { width: 100%; }
}

/* The disclosure is a button, not a selectable list item: no selection overlay. */
.assembly-trigger { display: block; width: 100%; text-align: left; background: white; color: inherit; border: 0; cursor: pointer; }
.assembly-trigger:hover { background: #fcfafd; }
.assembly-trigger:focus-visible { outline: 2px solid #806697; outline-offset: -4px; border-radius: 12px; }
.assembly-list--expanded .assembly-trigger { background: white; }
.assembly-details { background: white; padding: 0 24px 24px !important; }
.assembly-details .block-text { background: transparent; padding-top: 22px; }
.meeting-detail-description { margin-bottom: 22px; }
.detail-label { display: block; color: #8a7b95; font-size: 10px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 8px; }
.meeting-detail-description ::v-deep p:last-child { margin-bottom: 0; }
.participation-overview { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; padding: 20px; background: #faf8fc; border-radius: 10px; }
.participation-item { min-width: 0; }
.participation-item strong { color: #402c52; font-size: 14px; font-weight: 600; display: flex; align-items: center; gap: 6px; overflow-wrap: anywhere; }
.participation-item .participation-ok { color: #28643b; }
.assembly-actions { padding-top: 2px; margin-top: 20px; }
@media (max-width: 600px) {
  .assembly-details { padding: 0 16px 20px !important; }
  .participation-overview { grid-template-columns: 1fr; gap: 16px; padding: 16px; }
  .participation-item + .participation-item { border-top: 1px solid #eee8f2; padding-top: 14px; }
}

.next-assembly-card { border-radius: 18px !important; overflow: hidden; }
.next-assembly-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 26px 28px 22px; border-bottom: 1px solid #eee8f3; }
.next-assembly-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: .14em; color: #8b769d; }
.next-assembly-heading h2 { margin: 8px 0 0; font-size: 24px; line-height: 1.25; letter-spacing: -.025em; color: #29153d; }
.next-assembly-body { padding: 24px 28px; }
.next-assembly-schedule { display: flex; align-items: center; gap: 18px; padding: 20px; background: #f6f2fa; border-radius: 12px; }
.next-assembly-schedule > div { display: flex; flex-direction: column; gap: 6px; }
.next-assembly-schedule span { font-size: 9px; font-weight: 600; letter-spacing: .07em; color: #826b94; }
.next-assembly-schedule strong { font-size: 22px; color: #342044; line-height: 1.2; font-variant-numeric: tabular-nums; }
.next-assembly-schedule small { font-size: 12px; font-weight: 500; color: #7d688e; }
.next-assembly-time { margin-left: auto; padding-left: 18px; border-left: 1px solid #e0d6e9; }
.next-assembly-context { padding: 24px 0; }
.next-assembly-context .detail-label { font-size: 12px; letter-spacing: 0; text-transform: none; overflow-wrap: anywhere; }
.next-assembly-context h3 { font-size: 20px; color: #29153d; margin-bottom: 14px; line-height: 1.4; overflow-wrap: anywhere; }
.next-assembly-accreditation { display: flex; align-items: flex-start; gap: 12px; background: #f7f5fa; border: 1px solid #e9e3ef; border-radius: 10px; padding: 16px; color: #4b385a; }
.next-assembly-accreditation--approved { background: #f1f8f3; border-color: #dcebe1; color: #29653e; }
.next-assembly-accreditation strong { display: block; font-size: 14px; line-height: 1.6; }
.next-assembly-accreditation p { margin: 4px 0 0; font-size: 12px; line-height: 1.6; color: #6e6575; }
.next-assembly-footer { justify-content: flex-end; gap: 8px; padding: 16px 28px !important; border-top: 1px solid #eee8f3; }
.next-assembly-footer .v-btn { border-radius: 8px; text-transform: none; letter-spacing: 0; min-height: 40px; }
.next-assembly-upcoming { margin-top: 24px; }
.next-assembly-upcoming h3 { font-size: 14px; color: #4c355e; margin-bottom: 12px; }
.next-assembly-events { max-height: 220px; overflow-y: auto; }
.next-assembly-event { border-left: 2px solid #e2d7ec; padding: 0 0 18px 16px; margin-bottom: 8px; overflow-wrap: anywhere; }
.next-assembly-event > strong { color: #402a50; font-size: 14px; }
.next-assembly-event p { font-size: 12px; color: #7a6d84; margin: 6px 0; }
@media (max-width: 600px) {
 .next-assembly-heading { padding: 22px 20px 18px; }
 .next-assembly-heading h2 { font-size: 21px; }
 .next-assembly-body { padding: 20px; }
 .next-assembly-schedule { gap: 12px; padding: 16px; flex-wrap: wrap; }
 .next-assembly-schedule > .v-icon { display: none; }
 .next-assembly-schedule strong { font-size: 18px; }
 .next-assembly-time { padding-left: 12px; }
 .next-assembly-schedule span { font-size: 8px; }
 .next-assembly-footer { padding: 14px 20px !important; flex-wrap: wrap; }
 .next-assembly-footer .v-btn { margin-left: 0 !important; }
}

/* ---- Padrao novo (Meus credores / Minha conta / Resumo de casos) ---- */
.assembly-intro { padding: 32px 0 24px; }
.assembly-intro h1 { font-weight: 700; margin: 14px 0 10px; }
.assembly-search { border-color: rgba(24, 0, 50, .07); border-radius: 16px; background: #faf9fd; }
.assembly-search ::v-deep .v-text-field--outlined fieldset { border-color: rgba(126, 85, 171, .45); border-radius: 10px; }
.assembly-search ::v-deep .v-text-field--outlined:hover fieldset { border-color: #7e55ab; }
.assembly-search ::v-deep .v-input__slot { border-radius: 10px !important; background: #fff; }
.assembly-search .button-creditor-assembly { border-radius: 10px; font-weight: 600; background: var(--v-primary-base) !important; }
.assembly-section-heading { justify-content: flex-start; align-items: baseline; gap: 10px; margin: 28px 0 12px; }
.assembly-section-heading h2 { font-size: 13px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #7e55ab; }
.assembly-section-heading > span { font-size: 12px; color: rgba(24, 0, 50, .5); }
.assembly-list { border-color: rgba(24, 0, 50, .07); border-radius: 16px; }
.assembly-list--expanded { border-color: rgba(120, 119, 229, .4); box-shadow: 0 6px 20px rgba(24, 0, 50, .07); }
.assembly-date { background: rgba(120, 119, 229, .12); color: #3d2a66; }
.assembly-actions .v-btn, .presence-list-link .v-btn { border-radius: 10px; font-weight: 600; }
.participation-overview { background: #faf9fd; border-radius: 12px; }
.as-empty { padding: 18px 20px; border-radius: 14px; background: rgba(120, 119, 229, .07); color: #5e5667; font-size: 14px; }
.as-notice { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-radius: 12px; background: rgba(120, 119, 229, .09); color: #2a1a40; font-size: 14px; }
.as-muted { font-size: 13px; color: rgba(24, 0, 50, .55); }
.as-strong { font-weight: 600; color: #180032; }
.as-capitalize { text-transform: capitalize; }
.as-ok-text { color: #1e7b4f; font-weight: 600; }
.as-btn { height: 40px !important; padding: 0 18px !important; border-radius: 10px; text-transform: none; letter-spacing: 0; font-weight: 600; }
.as-action { text-transform: none; letter-spacing: normal; font-weight: 600; }
.as-pill { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; border: 0; }
.as-pill .v-icon { color: inherit; }
.as-pill__dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.as-pill--button { cursor: pointer; }
.as-pill--button:hover { filter: brightness(.96); }
.as-pill--ok { background: #e7f6ee; color: #1e7b4f; }
.as-pill--info { background: rgba(120, 119, 229, .14); color: #4b3a8f; }
.as-pill--warn { background: #fff4dc; color: #a16207; }
.as-pill--danger { background: #fdecea; color: #b42318; }
.as-pill--muted { background: #f1f0f4; color: #6b6475; }
.as-pill--outline { background: #fff; color: #7e55ab; box-shadow: inset 0 0 0 1px rgba(126, 85, 171, .4); }
.agc-shortcut { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 10px; background: #fff; border: 1px solid rgba(126, 85, 171, .3); color: #4b3a8f; font-size: 13px; font-weight: 600; text-decoration: none; transition: border-color .15s, background-color .15s; }
.agc-shortcut .v-icon { color: inherit; }
.agc-shortcut:hover { border-color: #7e55ab; background: rgba(120, 119, 229, .06); }
.agc-shortcuts { justify-content: center; margin: 20px 0 8px; }
.as-table ::v-deep th { font-size: 11px !important; font-weight: 700 !important; letter-spacing: .06em; text-transform: uppercase; color: #8b769d !important; border-bottom: 1px solid rgba(24, 0, 50, .08) !important; }
.as-table ::v-deep td { font-size: 13px !important; color: #25113c; border-bottom: 1px solid rgba(24, 0, 50, .05) !important; padding-top: 10px !important; padding-bottom: 10px !important; height: auto !important; }
.as-table ::v-deep tbody tr:hover { background: #faf9fd !important; }
.case-panels ::v-deep .case-panel { border: 1px solid rgba(24, 0, 50, .07) !important; border-radius: 16px !important; margin-bottom: 12px; }
.case-panels ::v-deep .case-panel::before { box-shadow: none; }
.case-panels ::v-deep .case-panel::after { border-top: 0 !important; }
.case-panels ::v-deep .case-panel > .v-expansion-panel-header { padding: 16px 20px; }
.case-panels ::v-deep .case-panel-title { color: #180032; }
.case-panels ::v-deep .case-panel-sub { margin-top: 2px; font-size: 13px; color: rgba(24, 0, 50, .55); }
.case-panels ::v-deep .case-panel-actions { margin-top: 16px; padding-top: 14px; border-top: 1px solid rgba(24, 0, 50, .06); align-items: center; }
</style>

<style>
.next-assembly-dialog { border-radius: 18px !important; }

/* Animate only height: padding and border stay inside the clipping wrapper. */
.assembly-collapse { padding: 0; border: 0; min-height: 0; overflow: hidden; }
.assembly-collapse.expand-transition-enter-active,
.assembly-collapse.expand-transition-leave-active { transition: height 200ms ease-in-out !important; }
@media (prefers-reduced-motion: reduce) {
  .assembly-collapse.expand-transition-enter-active,
  .assembly-collapse.expand-transition-leave-active { transition-duration: 1ms !important; }
}

.case-panels .case-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 10px;
}

.case-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  padding-right: 8px;
}

.case-panel-title {
  font-weight: 700;
  font-size: 15px;
}

.case-panel-sub {
  font-size: 12px;
  color: #6b7280;
}

.case-panel-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.case-panel-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.table-scroll {
  overflow-x: auto;
}

.agc-shortcuts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
</style>
